import React, { useEffect, useRef } from 'react';

import { Box, Grid } from '@mui/material';
import config from 'src/config';
import { AllVersion } from 'src/types/learningPath';

import EditCourseButton from './editCourseButton';

import Container from '@components/store/container';
import CourseRegularHeaderBannerBreadcrumb from '@components/templates/courseDetail/courseRegularBanner/header/breadCrumb';
import CourseRegularHeaderBannerHighlightCover from '@components/templates/courseDetail/courseRegularBanner/header/highlightCover';
import CourseRegularHeaderBannerPurchaseDetail from '@components/templates/courseDetail/courseRegularBanner/header/purchaseDetail';
import { GradientBackground } from '@components/templates/courseDetail/courseRegularBanner/style';
import useCourseRegularBanner from '@hooks/course/courseDetail/useCourseRegularBanner';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { useInsider } from '@hooks/dataTracking/useInsider';
import { ICategory } from '@interfaces/category';
import { CourseEntity } from '@redux/course/type/courseDetailType';

type CourseRegularHeaderBannerTypes = {
  onHeaderSizeChange: (height: number) => void;
  learningPath?: AllVersion;
  learningPathCategory?: ICategory;
  courseCount?: string;
  courseDetailSeo: CourseEntity;
};
const CourseRegularHeaderBanner = ({
  onHeaderSizeChange,
  learningPath,
  learningPathCategory,
  courseCount,
  courseDetailSeo,
}: CourseRegularHeaderBannerTypes) => {
  const courseRegularHeaderBannerRef = useRef<HTMLElement>();
  const { bannerPrimaryColor, bannerSecondaryColor } = useCourseRegularBanner();
  const { name, idName, id } = courseDetailSeo;
  const { courseDetailData } = useCourseDetail();
  const { insider_dispatch, sendProductPageView } = useInsider();
  const insiderRef = useRef<boolean>(false);

  useEffect(() => {
    if (!courseRegularHeaderBannerRef?.current?.offsetHeight) {
      return;
    }
    onHeaderSizeChange(courseRegularHeaderBannerRef?.current?.offsetHeight);
  }, [courseRegularHeaderBannerRef, onHeaderSizeChange]);

  useEffect(() => {
    if (!courseDetailData) {
      return;
    }

    const fullPrice = courseDetailData.price.regular?.fullPrice;
    const salePrice = courseDetailData.price.regular?.salePrice;

    if (!fullPrice || !salePrice) {
      return;
    }

    if (insiderRef.current) {
      return;
    }

    insiderRef.current = true;

    insider_dispatch([
      () =>
        sendProductPageView({
          id: id.toString(),
          name,
          unit_price: fullPrice,
          unit_sale_price: salePrice,
          url: `${config.APP_DOMAIN}/courses/${idName}`,
          product_image_url: courseDetailData.highlightImage2xUrl,
          taxonomy: [courseDetailData.mainCategory, courseDetailData.subCategory],
        }),
    ]);

    setTimeout(() => {
      insiderRef.current = false;
    }, 1000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseDetailData]);

  return (
    <GradientBackground
      ref={courseRegularHeaderBannerRef}
      primaryColor={bannerPrimaryColor}
      secondaryColor={bannerSecondaryColor}
    >
      <Box
        sx={{
          padding: {
            lg: '24px 0px 76px 0px',
            xs: '24px',
          },
        }}
      >
        <Container>
          <Grid container spacing={2} sx={{ mb: { md: '36px', xs: '24px' } }}>
            <Grid item md={9} xs={7}>
              <CourseRegularHeaderBannerBreadcrumb
                learningPath={learningPath}
                learningPathCategory={learningPathCategory}
                courseName={name}
                courseCount={courseCount}
              />
            </Grid>
            <Grid item md={3} xs={5} width="100%" display="flex" justifyContent="flex-end">
              <EditCourseButton courseIdName={idName} />
            </Grid>
          </Grid>

          <Box px={{ md: 6, lg: 0 }} display="grid" gridTemplateColumns={{ lg: '487px 1fr' }} gap={{ xs: 3, lg: 10 }}>
            <CourseRegularHeaderBannerHighlightCover courseDetailSeo={courseDetailSeo} />
            <CourseRegularHeaderBannerPurchaseDetail courseDetailSeo={courseDetailSeo} />
          </Box>
        </Container>
      </Box>
    </GradientBackground>
  );
};

export default CourseRegularHeaderBanner;
